"use client";

import { useState, useRef } from "react";

const font = "'IBM Plex Mono', monospace";

interface DataPoint {
  label: string;
  score: number;
  cost: number;
  color: string;
  highlight?: boolean;
  /** Manual label offset to avoid overlap: [dx, dy] from default position */
  labelOffset?: [number, number];
}

const data: DataPoint[] = [
  {
    label: "Gemini 3.1 Pro (Preview)",
    score: 0.2,
    cost: 2200,
    color: "#fff",
    labelOffset: [0, 30],
  },
  {
    label: "Grok 4.20 (Beta Reasoning)",
    score: 0.0,
    cost: 3800,
    color: "#fff",
    labelOffset: [0, -40],
  },
  {
    label: "GPT-5.4 (High)",
    score: 0.3,
    cost: 5200,
    color: "#fff",
    labelOffset: [0, 30],
  },
  {
    label: "Opus 4.6 (Max)",
    score: 0.2,
    cost: 8900,
    color: "#fff",
    labelOffset: [0, -40],
  },
  {
    label: "Agentica Opus 4.6 (High)",
    score: 36.08,
    cost: 1000,
    color: "#8a84ff",
    highlight: true,
  },
];

const W = 960,
  H = 560;
const pad = { t: 55, r: 30, b: 100, l: 80 };
const pW = W - pad.l - pad.r;
const pH = H - pad.t - pad.b;

const maxScore = 40;
const xMin = Math.log10(1);
const xMax = Math.log10(10000);

const xScale = (cost: number) =>
  pad.l + ((Math.log10(Math.max(cost, 1)) - xMin) / (xMax - xMin)) * pW;
const yScale = (score: number) => pad.t + pH - (score / maxScore) * pH;

const yTicks = [0, 10, 20, 30, 40];
const xTicks = [1, 10, 100, 1000, 10000];

const colors = {
  text: "#fff",
  axisLabel: "#999",
  axisTitle: "#a3a3a3",
  gridLine: "#222",
  axisLine: "#444",
};

const costLabel = (v: number) =>
  v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`;

export function LevelChart() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    item: DataPoint | null;
  }>({ visible: false, x: 0, y: 0, item: null });

  const handleMouseEnter = (item: DataPoint, e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 230 > rect.width) x = e.clientX - rect.left - 240;
    if (y + 120 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip({ visible: true, x, y, item });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!tooltip.visible || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 230 > rect.width) x = e.clientX - rect.left - 240;
    if (y + 120 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip((prev) => ({ ...prev, x, y }));
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, item: null });
  };

  return (
    <div ref={containerRef} style={{ width: "100%", position: "relative" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Title */}
        <text
          x={W / 2}
          y={pad.t - 14}
          textAnchor="middle"
          fill={colors.text}
          style={{ fontSize: "17px", fontFamily: font, fontWeight: 400 }}
        >
          ARC-AGI-3 Public Eval: Score vs Cost
        </text>

        {/* Y grid lines */}
        {yTicks.map((v) => (
          <line
            key={`yg-${v}`}
            x1={pad.l}
            y1={yScale(v)}
            x2={W - pad.r}
            y2={yScale(v)}
            stroke={colors.gridLine}
            strokeWidth={1}
          />
        ))}

        {/* Y axis labels */}
        {yTicks.map((v) => (
          <text
            key={`yl-${v}`}
            x={pad.l - 12}
            y={yScale(v) + 4}
            textAnchor="end"
            fill={colors.axisLabel}
            style={{ fontSize: "13px", fontFamily: font }}
          >
            {v}%
          </text>
        ))}

        {/* Y axis title */}
        <text
          x={20}
          y={pad.t + pH / 2}
          textAnchor="middle"
          transform={`rotate(-90, 20, ${pad.t + pH / 2})`}
          fill={colors.axisTitle}
          style={{ fontSize: "15px", fontFamily: font }}
        >
          Score (%)
        </text>

        {/* X axis line */}
        <line
          x1={pad.l}
          y1={yScale(0)}
          x2={W - pad.r}
          y2={yScale(0)}
          stroke={colors.axisLine}
          strokeWidth={1}
        />

        {/* X axis labels */}
        {xTicks.map((v) => (
          <text
            key={`xl-${v}`}
            x={xScale(v)}
            y={yScale(0) + 20}
            textAnchor="middle"
            fill={colors.axisLabel}
            style={{ fontSize: "13px", fontFamily: font }}
          >
            {v >= 1000 ? `$${v / 1000}k` : `$${v}`}
          </text>
        ))}

        {/* X axis title */}
        <text
          x={pad.l + pW / 2}
          y={H - 10}
          textAnchor="middle"
          fill={colors.axisTitle}
          style={{ fontSize: "15px", fontFamily: font }}
        >
          Cost ($)
        </text>

        {/* Data points */}
        {data.map((d) => {
          const cx = xScale(d.cost);
          const cy = yScale(d.score);
          const fontSize = d.highlight ? 12 : 10;
          const lineHeight = fontSize + 2;
          const [dx, dy] = d.labelOffset ?? [0, 0];
          const labelX = cx + dx;
          const labelY = cy + dy;
          return (
            <g
              key={d.label}
              onMouseEnter={(e) => handleMouseEnter(d, e)}
              style={{ cursor: "pointer" }}
            >
              {/* Invisible larger hit area */}
              <circle cx={cx} cy={cy} r={20} fill="transparent" />
              {d.highlight && (
                <>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={16}
                    fill={d.color}
                    opacity={0.15}
                  />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={14}
                    fill="none"
                    stroke="#E53935"
                    strokeWidth={1.5}
                    opacity={0.5}
                  />
                  <text
                    x={cx + 20}
                    y={cy - 2}
                    textAnchor="start"
                    fill="#E53935"
                    style={{
                      fontSize: "10px",
                      fontFamily: font,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                    }}
                  >
                    SOTA
                  </text>
                </>
              )}
              <circle
                cx={cx}
                cy={cy}
                r={6}
                fill={d.color}
                stroke={d.highlight ? "#E53935" : d.color}
                strokeWidth={d.highlight ? 3 : 2}
              />
              {/* Label */}
              {(() => {
                const words = d.label.split(" ");
                let lines: string[];
                if (d.highlight) {
                  const m = Math.ceil(words.length / 2);
                  lines = [
                    words.slice(0, m).join(" "),
                    words.slice(m).join(" "),
                  ];
                } else {
                  const third = Math.ceil(words.length / 3);
                  const twoThirds = Math.ceil((words.length * 2) / 3);
                  lines = [
                    words.slice(0, third).join(" "),
                    words.slice(third, twoThirds).join(" "),
                    words.slice(twoThirds).join(" "),
                  ].filter(Boolean);
                }
                const startY = d.highlight
                  ? labelY - 28 - (lines.length - 1) * lineHeight
                  : labelY + 4;
                return (
                  <text
                    x={labelX}
                    y={startY}
                    textAnchor="middle"
                    fill={d.color}
                    style={{
                      fontSize: `${fontSize}px`,
                      fontFamily: font,
                      fontWeight: d.highlight ? 600 : 400,
                    }}
                  >
                    {lines.map((line, i) => (
                      <tspan key={i} x={labelX} dy={i === 0 ? 0 : lineHeight}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                );
              })()}
              {/* Connector line from label to dot (non-highlight only) */}
              {!d.highlight && dy !== 0 && (
                <line
                  x1={cx}
                  y1={cy + (dy > 0 ? 8 : -8)}
                  x2={labelX}
                  y2={dy > 0 ? labelY - 6 : labelY + lineHeight * 2 + 6}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth={1}
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip.visible && tooltip.item && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            pointerEvents: "none",
            background: "#1e1e1e",
            border: "1px solid #2a2a2a",
            borderRadius: "10px",
            padding: "14px 18px",
            zIndex: 100,
            minWidth: "220px",
            boxShadow: "0 4px 20px rgba(0,0,0,.4)",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "3px",
              borderRadius: "2px",
              marginBottom: "10px",
              background: tooltip.item.color,
            }}
          />
          <div
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "#fff",
              marginBottom: "10px",
              lineHeight: 1.3,
              fontFamily: font,
            }}
          >
            {tooltip.item.label}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "4px",
              fontSize: "0.72rem",
              fontFamily: font,
            }}
          >
            <span style={{ color: "#888", fontWeight: 300 }}>Score</span>
            <span style={{ color: "#e0e0e0", fontWeight: 400 }}>
              {tooltip.item.score}%
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "4px",
              fontSize: "0.72rem",
              fontFamily: font,
            }}
          >
            <span style={{ color: "#888", fontWeight: 300 }}>Cost</span>
            <span style={{ color: "#e0e0e0", fontWeight: 400 }}>
              {costLabel(tooltip.item.cost)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
